import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { apis } from "../../../core/services/params";

export interface OfferPriceItem {
    id: number;
    msn: string;
    mrp: number;
    offerPrice: number;
    priceWithoutTax: number;
    taxPercentage: number;
    discount: number;
    startDate: number;
    endDate: number;
    status: boolean;
    showPrice: boolean;
    createdBy: string;
    createdAt: number;
    updatedAt: number;
}

const OfferPriceTab = ({ user }: { user: any }) => {
    const [data, setData] = useState<OfferPriceItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [filters, setFilters] = useState({
        msn: "",
        status: "",
        showPrice: "",
        startDate: "",
        endDate: ""
    });

    // Modal States
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'update'>('create');
    const [formData, setFormData] = useState({
        id: 0,
        msn: "",
        offerPrice: "",
        startDate: "",
        endDate: "",
        status: true,
        showPrice: true
    });
    const [saving, setSaving] = useState(false);

    // Bulk Upload States
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadResult, setUploadResult] = useState<any>(null);
    const [showUploadResultModal, setShowUploadResultModal] = useState(false);

    const fetchData = async (page = 1, appliedFilters = filters) => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                size: pageSize.toString(),
                ...(appliedFilters.msn && { msn: appliedFilters.msn }),
                ...(appliedFilters.status && { status: appliedFilters.status }),
                ...(appliedFilters.showPrice && { showPrice: appliedFilters.showPrice }),
                ...(appliedFilters.startDate && { startDate: appliedFilters.startDate }),
                ...(appliedFilters.endDate && { endDate: appliedFilters.endDate })
            });

            const response = await fetch(
                `${apis.FLASH_DEAL_LIST}?${queryParams}`,
                { method: "GET" }
            );

            if (!response.ok) throw new Error("Failed to fetch data");

            const result = await response.json();
            if (result.status) {
                setData(result.data || []);
                setTotalPages(result.totalPage || 1);
                setTotalCount(result.totalCount || 0);
                setCurrentPage(result.currentPage || page);
                setPageSize(result.pageSize || 10);
            } else {
                toast.error(result.statusDescription || "Failed to fetch data");
                setData([]);
            }
        } catch (error) {
            console.error("Error fetching offer price data:", error);
            toast.error("Failed to fetch offer price data");
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(currentPage, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (field: string, value: string) => {
        setFilters({ ...filters, [field]: value });
    };

    const handleDropdownFilterChange = (field: string, value: string) => {
        const newFilters = { ...filters, [field]: value };
        setFilters(newFilters);
        setCurrentPage(1);
        fetchData(1, newFilters);
    };

    const applyFilters = () => {
        setCurrentPage(1);
        fetchData(1, filters);
    };

    const clearFilters = () => {
        const emptyFilters = { msn: "", status: "", showPrice: "", startDate: "", endDate: "" };
        setFilters(emptyFilters);
        setCurrentPage(1);
        fetchData(1, emptyFilters);
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') applyFilters();
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchData(page, filters);
    };

    // Form logic
    const openCreateModal = () => {
        setModalMode('create');
        const today = new Date().toISOString().split('T')[0];
        setFormData({
            id: 0,
            msn: "",
            offerPrice: "",
            startDate: `${today}T00:00:00`,
            endDate: `${today}T23:59:59`,
            status: true,
            showPrice: true
        });
        setShowModal(true);
    };

    const openUpdateModal = (item: OfferPriceItem) => {
        setModalMode('update');
        const toLocalDateTimeString = (ts: number) => {
            const d = new Date(ts);
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const hh = String(d.getHours()).padStart(2, '0');
            const min = String(d.getMinutes()).padStart(2, '0');
            const ss = String(d.getSeconds()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
        };
        setFormData({
            id: item.id,
            msn: item.msn,
            offerPrice: item.offerPrice.toString(),
            startDate: toLocalDateTimeString(item.startDate),
            endDate: toLocalDateTimeString(item.endDate),
            status: item.status,
            showPrice: item.showPrice
        });
        setShowModal(true);
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const endpoint = modalMode === 'create' ? apis.FLASH_DEAL_CREATE : apis.FLASH_DEAL_UPDATE;
            const email = encodeURIComponent(user?.email || 'admin@moglix.com');
            const url = `${endpoint}?createdBy=${email}`;

            const formatApiDate = (dateStr: string) => {
                if (!dateStr) return "";
                const formatted = dateStr.replace('T', ' ');
                // If format is YYYY-MM-DD HH:mm (16 chars), append :00
                return formatted.length === 16 ? `${formatted}:00` : formatted;
            };

            const payload: any = {
                msn: formData.msn,
                offerPrice: Number(formData.offerPrice),
                startDate: formatApiDate(formData.startDate),
                endDate: formatApiDate(formData.endDate),
                status: formData.status,
                showPrice: formData.showPrice
            };

            if (modalMode === 'update') {
                payload.id = formData.id;
            }

            const response = await fetch(url, {
                method: "POST",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result.status) {
                toast.success(result.statusDescription || `Offer ${modalMode === 'create' ? 'created' : 'updated'} successfully`);
                setShowModal(false);
                fetchData(currentPage, filters);
            } else {
                toast.error(result.statusDescription || `Failed to ${modalMode} offer`);
            }
        } catch (error) {
            console.error(`Error saving offer:`, error);
            toast.error(`Error saving offer`);
        } finally {
            setSaving(false);
        }
    };

    // Bulk Upload logic
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.name.toLowerCase().endsWith('.csv')) {
                toast.error("Please select a valid CSV file");
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleFileUpload = async () => {
        if (!selectedFile) return;
        setUploadLoading(true);

        const formDataData = new FormData();
        formDataData.append('file', selectedFile);

        try {
            const email = encodeURIComponent(user?.email || 'admin@moglix.com');
            const response = await fetch(`${apis.UPLOAD_FLASH_DEAL}?createdBy=${email}`, {
                method: 'POST',
                body: formDataData,
            });

            if (!response.ok) throw new Error("Upload failed");

            const result = await response.json();

            if (result.status) {
                setUploadResult(result.data);
                setShowUploadResultModal(true);
                setSelectedFile(null);
                const fileInput = document.getElementById('offerPriceCsv') as HTMLInputElement;
                if (fileInput) fileInput.value = '';
                fetchData(currentPage, filters);
            } else {
                toast.error(result.statusDescription || "Upload failed");
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error("Error occurred during upload.");
        } finally {
            setUploadLoading(false);
        }
    };

    const downloadTemplate = () => {
        const csvHeaders = "MSN, Offer Price, Start Date, End Date, Status, Show Price";
        const sampleData = "MSN123456789,1600,2026-03-30 00:00:00,2026-04-30 23:59:59,true,true";
        const csvContent = `${csvHeaders}\n${sampleData}`;
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'flash_deal_template.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    const getPaginationNumbers = () => {
        const numbers = [];
        const maxVisible = 10;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);

        if (end - start < maxVisible - 1) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) numbers.push(i);
        return numbers;
    };

    return (
        <>
            {/* Top Upload Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Bulk Upload Flash Deal</h4>
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex items-center">
                                <input
                                    type="file"
                                    id="offerPriceCsv"
                                    accept=".csv"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                                <label
                                    htmlFor="offerPriceCsv"
                                    className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    Choose File
                                </label>
                                {selectedFile && (
                                    <span className="ml-3 text-sm text-gray-600 truncate max-w-xs block">
                                        {selectedFile.name}
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={downloadTemplate}
                                className="inline-flex items-center px-4 py-2 border border-green-300 text-green-700 rounded-md hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-medium"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download Template
                            </button>

                            <button
                                onClick={handleFileUpload}
                                disabled={!selectedFile || uploadLoading}
                                className="inline-flex items-center px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {uploadLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
                                            <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" className="opacity-75"></path>
                                        </svg>
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        Upload Flash Deal
                                    </>
                                )}
                            </button>
                        </div>
                        <button
                            onClick={openCreateModal}
                            className="bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ml-auto whitespace-nowrap"
                        >
                            + Create Flash Deal
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 py-4 px-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 items-end">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">MSN</label>
                        <input
                            type="text"
                            value={filters.msn}
                            onChange={(e) => handleFilterChange('msn', e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Filter by MSN"
                            className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                        <select
                            value={filters.status}
                            onChange={(e) => handleDropdownFilterChange('status', e.target.value)}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">All Status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Show Price</label>
                        <select
                            value={filters.showPrice}
                            onChange={(e) => handleDropdownFilterChange('showPrice', e.target.value)}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">All</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            onKeyPress={handleKeyPress}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                        <input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            onKeyPress={handleKeyPress}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div className="flex space-x-2 sm:col-span-2">
                        <button
                            onClick={applyFilters}
                            className="flex-1 whitespace-nowrap text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 font-medium h-[34px] shadow-sm"
                        >
                            Apply Filters
                        </button>
                        <button
                            onClick={clearFilters}
                            className="flex-1 whitespace-nowrap text-sm text-blue-600 bg-white border border-blue-600 px-3 py-1.5 rounded hover:bg-blue-50 font-medium h-[34px] shadow-sm"
                        >
                            Clear All
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-800 text-white">
                            <tr>
                                <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">MSN</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">MRP</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Flash Deal Price</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Price W/O Tax</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Tax %</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Discount</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Start Date</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">End Date</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Status</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Show Price</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Created By</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Updated At</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Action</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={13} className="px-6 py-4 text-center text-sm text-gray-500">
                                        <div className="flex justify-center items-center">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                            <span className="ml-2">Loading data...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={13} className="px-6 py-8 text-center text-gray-500">No data found</td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{item.msn}</td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">₹{item.mrp?.toFixed(2) || '0.00'}</td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs font-bold text-gray-900">₹{item.offerPrice.toFixed(2)}</td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">₹{item.priceWithoutTax?.toFixed(2)}</td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{item.taxPercentage}%</td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-green-600">{item.discount}%</td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                            {new Date(item.startDate).toLocaleString('en-GB')}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                            {new Date(item.endDate).toLocaleString('en-GB')}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-[10px] font-semibold rounded-full ${item.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {item.status ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-[10px] font-semibold rounded-full ${item.showPrice ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>
                                                {item.showPrice ? 'Yes' : 'No'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">{item.createdBy}</td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                            {item.updatedAt ? new Date(item.updatedAt).toLocaleString('en-GB') : '-'}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs">
                                            <button
                                                onClick={() => openUpdateModal(item)}
                                                className="text-blue-600 hover:text-blue-900 border border-blue-200 bg-blue-50 px-2 py-1 rounded"
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="text-sm text-gray-700">
                                Displaying {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount.toLocaleString()} results
                            </div>
                            <div className="flex items-center space-x-2">
                                {currentPage > 1 && (
                                    <button onClick={() => handlePageChange(currentPage - 1)} className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 bg-white text-gray-700">
                                        Previous
                                    </button>
                                )}
                                {getPaginationNumbers().map((pageNum) => (
                                    <button
                                        key={pageNum}
                                        onClick={() => handlePageChange(pageNum)}
                                        className={`px-3 py-2 text-sm border rounded ${currentPage === pageNum ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 hover:bg-gray-50 bg-white text-gray-700'}`}
                                    >
                                        {pageNum}
                                    </button>
                                ))}
                                {currentPage < totalPages && (
                                    <button onClick={() => handlePageChange(currentPage + 1)} className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 bg-white text-gray-700">
                                        Next
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Create / Update Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
                    <div className="relative p-8 border w-full max-w-md shadow-lg rounded-md bg-white">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold text-gray-900 text-left">
                                {modalMode === 'create' ? 'Create Flash Deal' : 'Update Flash Deal'}
                            </h3>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        <form onSubmit={handleFormSubmit}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 text-left">MSN *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.msn}
                                        onChange={(e) => setFormData({ ...formData, msn: e.target.value.toUpperCase() })}
                                        disabled={modalMode === 'update'}
                                        className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 ${modalMode === 'update' ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                                        placeholder="E.g., MSNQ94PWR3ENK0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Status *</label>
                                    <select
                                        value={formData.status.toString()}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value === 'true' })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Show Price *</label>
                                    <select
                                        value={formData.showPrice.toString()}
                                        onChange={(e) => setFormData({ ...formData, showPrice: e.target.value === 'true' })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Flash Deal Price *</label>
                                    <input
                                        type="number"
                                        required
                                        min="0"
                                        step="0.01"
                                        value={formData.offerPrice}
                                        onChange={(e) => setFormData({ ...formData, offerPrice: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="E.g., 400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Start Date *</label>
                                    <input
                                        type="datetime-local"
                                        step="1"
                                        required
                                        min={modalMode === 'create' ? new Date().toISOString().split('T')[0] + "T00:00:00" : undefined}
                                        value={formData.startDate}
                                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 text-left">End Date *</label>
                                    <input
                                        type="datetime-local"
                                        step="1"
                                        required
                                        min={modalMode === 'create' ? (formData.startDate || new Date().toISOString().split('T')[0] + "T00:00:00") : formData.startDate}
                                        value={formData.endDate}
                                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="mt-8 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : (modalMode === 'create' ? 'Create' : 'Update')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Bulk Upload Result Modal */}
            {showUploadResultModal && uploadResult && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
                    <div className="relative p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-900">Bulk Upload Results</h3>
                            <button onClick={() => setShowUploadResultModal(false)} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-6">
                            <div className="bg-gray-50 p-4 rounded border text-center">
                                <div className="text-sm text-gray-500 font-medium">Total Records</div>
                                <div className="text-2xl font-bold text-gray-900">{uploadResult.totalRecords}</div>
                            </div>
                            <div className="bg-green-50 p-4 rounded border border-green-200 text-center">
                                <div className="text-sm text-green-600 font-medium">Success</div>
                                <div className="text-2xl font-bold text-green-700">{uploadResult.successCount}</div>
                            </div>
                            <div className="bg-red-50 p-4 rounded border border-red-200 text-center">
                                <div className="text-sm text-red-600 font-medium">Failed</div>
                                <div className="text-2xl font-bold text-red-700">{uploadResult.failedCount}</div>
                            </div>
                        </div>

                        {uploadResult.failedCount > 0 && uploadResult.errors && (
                            <div className="mt-4">
                                <h4 className="text-md font-semibold text-gray-800 mb-2">Error Details</h4>
                                <div className="max-h-60 overflow-y-auto border rounded bg-gray-50">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">MSN</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Error Message</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {uploadResult.errors.map((err: any, idx: number) => (
                                                <tr key={idx}>
                                                    <td className="px-4 py-2 text-sm text-gray-900">{err.row}</td>
                                                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{err.msn}</td>
                                                    <td className="px-4 py-2 text-sm text-red-600">{err.error}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setShowUploadResultModal(false)}
                                className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-900"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OfferPriceTab;
